import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  makeStyles,
  Typography,
  Button,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CloseIcon from '@material-ui/icons/Close';
import { toast } from 'react-toastify';
import { toastSuccess, toastInfo } from '../../errors/toastSuccess';
import usePushSubscription from '../../hooks/usePushSubscription';
import { usePlanPermissions } from '../../context/PlanPermissionsContext';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 16,
    boxShadow: '0 8px 32px rgba(15,23,42,0.14)',
    overflow: 'hidden',
    width: '100%',
    maxWidth: 480,
    [theme.breakpoints.down('sm')]: {
      maxWidth: '95vw',
      margin: theme.spacing(1),
    },
  },
  dialogHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: '1px solid #e8edf2',
    backgroundColor: '#fff',
    flexShrink: 0,
  },
  dialogHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    minWidth: 0,
  },
  dialogHeaderIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    backgroundColor: '#f0fdf4',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& svg': { fontSize: 22, color: '#16a34a' },
  },
  dialogHeaderTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    color: '#0f172a',
    lineHeight: 1.3,
  },
  dialogHeaderSubtitle: {
    fontSize: '0.8rem',
    color: '#64748b',
    marginTop: 1,
  },
  dialogCloseButton: {
    color: '#94a3b8',
    padding: 6,
    borderRadius: 8,
    flexShrink: 0,
    '&:hover': { backgroundColor: '#f1f5f9', color: '#334155' },
  },
  dialogContent: {
    padding: '20px 24px',
    backgroundColor: '#f8fafc',
  },
  dialogActions: {
    padding: '12px 20px',
    backgroundColor: '#fff',
    borderTop: '1px solid #e8edf2',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
  },
  dialogBtn: {
    borderRadius: 8,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.875rem',
    height: 36,
    minWidth: 100,
  },
  infoCard: {
    backgroundColor: '#fff',
    borderRadius: 10,
    border: '1px solid #e8edf2',
    padding: '14px 16px',
    marginBottom: 12,
  },
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: '0.875rem',
    color: '#475569',
  },
  notSupported: {
    backgroundColor: '#fffbeb',
    border: '1px solid #fde68a',
    borderRadius: 10,
    padding: '12px 14px',
    fontSize: '0.875rem',
    color: '#92400e',
    lineHeight: 1.6,
  },
  errorCard: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: 10,
    padding: '12px 14px',
    fontSize: '0.875rem',
    color: '#991b1b',
  },
}));

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

const NotificationModal = ({ open, onClose }) => {
  const classes = useStyles();
  const { planActive } = usePlanPermissions();
  const { isSupported, isSubscribed, subscribe, unsubscribe, loading, error, permission } =
    usePushSubscription();
  const iosDevice = typeof navigator !== 'undefined' && isIOS();

  const handleToggle = async () => {
    if (loading) return;
    if (isSubscribed) {
      await unsubscribe();
      toastInfo('toastInfo.notificationsDisabled');
    } else {
      const ok = await subscribe();
      if (ok) toastSuccess('toastSuccess.notificationsEnabled');
      else
        toast.error(
          error || 'Não foi possível ativar. Use HTTPS e permita notificações no navegador.'
        );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: classes.dialogPaper }}
    >
      <Box className={classes.dialogHeader}>
        <Box className={classes.dialogHeaderLeft}>
          <Box className={classes.dialogHeaderIcon}>
            <NotificationsIcon />
          </Box>
          <Box minWidth={0}>
            <Typography className={classes.dialogHeaderTitle} component="h2">
              Notificações push
            </Typography>
            <Typography className={classes.dialogHeaderSubtitle} component="p">
              Receba avisos de novos leads e mensagens
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Fechar">
          <IconButton
            className={classes.dialogCloseButton}
            size="small"
            onClick={onClose}
            aria-label="Fechar"
          >
            <CloseIcon style={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <DialogContent className={classes.dialogContent}>
        <Box className={classes.infoCard}>
          <Typography style={{ fontSize: '0.875rem', color: '#334155', marginBottom: 12 }}>
            Ative as notificações <strong>neste navegador</strong> (ou no celular) para receber
            avisos de novos atendimentos e mensagens.
          </Typography>

          {!isSupported ? (
            <Box className={classes.notSupported}>
              {iosDevice ? (
                <>
                  No iPhone e iPad a <strong>Apple só permite</strong> notificações push quando o
                  site é aberto pela Tela de Início.
                  <Box component="p" mt={1} mb={0}>
                    Toque em <strong>Compartilhar</strong> no Safari → escolha{' '}
                    <strong>"Adicionar à Tela de Início"</strong> → abra pelo ícone e ative aqui.
                  </Box>
                </>
              ) : (
                <>
                  Não disponível neste dispositivo. Use HTTPS e um navegador compatível (Chrome,
                  Firefox, Edge ou Safari).
                </>
              )}
            </Box>
          ) : (
            <>
              {!planActive && (
                <Box className={classes.errorCard} mb={1}>
                  Regularize suas faturas para ativar notificações push.
                </Box>
              )}

              {permission === 'denied' && (
                <Box className={classes.errorCard}>
                  Notificações bloqueadas. Habilite nas configurações do navegador para este site.
                </Box>
              )}

              {permission === 'granted' && (
                <Box className={classes.statusRow}>
                  {isSubscribed ? (
                    <NotificationsActiveIcon style={{ fontSize: 18, color: '#16a34a' }} />
                  ) : (
                    <NotificationsOffIcon style={{ fontSize: 18, color: '#94a3b8' }} />
                  )}
                  <span>{isSubscribed ? 'Notificações ativadas' : 'Notificações desativadas'}</span>
                </Box>
              )}
            </>
          )}
        </Box>
      </DialogContent>

      {isSupported && (
        <DialogActions className={classes.dialogActions}>
          <Button className={classes.dialogBtn} onClick={onClose}>
            Fechar
          </Button>
          <Button
            className={classes.dialogBtn}
            variant="contained"
            color={isSubscribed ? 'default' : 'primary'}
            onClick={handleToggle}
            disabled={loading || permission === 'denied' || !planActive}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {loading ? 'Aguarde...' : isSubscribed ? 'Desativar' : 'Ativar notificações'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default NotificationModal;
